import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";

import * as UI from "../../../../../modules/ui-kit";
import EntityCreateForm from "../../../components/EntityCreateForm";
import {
    PropertiesField,
    RequirementGroupsField,
    RequirementsField,
    UrlListField,
} from "../../../components/fields";
import ListBimClasses from "../BimClassInventory/ListBimClasses.graphql";
import DefineBimClass from "../DefineBimClass.graphql";

const DEFAULT_VALUES = {
    bimClassID: "",
    deprecated: false,
    materialName: [],
    name: [],
    previews: [],
    properties: [],
    requirementGroups: [],
    requirements: [],
    type: "",
};

function toDefinition(values) {
    const definition: Record<string, unknown> = {
        deprecated: values.deprecated,
        name: values.name,
    };
    if (values.materialName.length) definition.materialName = values.materialName;
    if (values.bimClassID) definition.bimClassID = values.bimClassID;
    if (values.type) definition.type = values.type;
    // Only send the collections that were actually filled in — an empty array
    // is a meaningful "cleared" value on update, but noise on create.
    if (values.previews.length) definition.previews = values.previews;
    if (values.requirementGroups.length) definition.requirementGroups = values.requirementGroups;
    if (values.requirements.length) definition.requirements = values.requirements;
    if (values.properties.length) definition.properties = values.properties;
    return definition;
}

function renderFields({ saving, setValue, values }) {
    return (
        <>
            <UI.Field>
                <UI.FieldLabel>name</UI.FieldLabel>
                <UI.FieldDescription>Multi-language name of this BIM Class</UI.FieldDescription>
                <UI.TranslatedInput
                    disabled={ saving }
                    label="name"
                    onChange={ (val) => setValue("name", val) }
                    value={ values.name }
                />
            </UI.Field>

            <UI.Field>
                <UI.FieldLabel>materialName</UI.FieldLabel>
                <UI.FieldDescription>Multi-language material designation</UI.FieldDescription>
                <UI.TranslatedInput
                    disabled={ saving }
                    label="materialName"
                    onChange={ (val) => setValue("materialName", val) }
                    value={ values.materialName }
                />
            </UI.Field>

            <UI.Field>
                <UI.FieldLabel>bimClassID</UI.FieldLabel>
                <UI.FieldDescription>Unique identifier for this BIM Class</UI.FieldDescription>
                <UI.TextInput
                    disabled={ saving }
                    onChange={ (val) => setValue("bimClassID", val) }
                    placeholder="e.g. wall_standard"
                    value={ values.bimClassID }
                />
            </UI.Field>

            <UI.Field>
                <UI.FieldLabel>type</UI.FieldLabel>
                <UI.FieldDescription>BIM Class type designation</UI.FieldDescription>
                <UI.TextInput
                    disabled={ saving }
                    onChange={ (val) => setValue("type", val) }
                    placeholder="e.g. ELEMENT / MATERIAL"
                    value={ values.type }
                />
            </UI.Field>

            <UI.Field>
                <UI.FieldLabel>deprecated</UI.FieldLabel>
                <UI.FieldDescription>Toggle whether this BIM Class is active or deprecated</UI.FieldDescription>
                <UI.SegmentedControl
                    disabled={ saving }
                    onChange={ (val) => setValue("deprecated", val) }
                    options={ [
                        { label: "Deprecated", value: true },
                        { label: "Not deprecated", value: false },
                    ] }
                    value={ values.deprecated }
                />
            </UI.Field>

            <UrlListField
                addLabel="Add Preview Image URL"
                description="Preview image URLs illustrating this BIM Class"
                disabled={ saving }
                label="previews"
                onChange={ (val) => setValue("previews", val) }
                value={ values.previews }
            />

            <RequirementGroupsField
                description="Logical groups organizing parameters for this BIM Class"
                disabled={ saving }
                label="requirementGroups"
                onChange={ (val) => setValue("requirementGroups", val) }
                value={ values.requirementGroups }
            />

            <RequirementsField
                description="Parameters required or associated with this BIM Class"
                disabled={ saving }
                label="requirements"
                onChange={ (val) => setValue("requirements", val) }
                value={ values.requirements }
            />

            <PropertiesField
                description="Assigned parameter properties and values (parameter.id &amp; value)"
                disabled={ saving }
                label="properties"
                onChange={ (val) => setValue("properties", val) }
                value={ values.properties }
            />
        </>
    );
}

export default function BimClassNew() {
    const [defineBimClass] = useMutation(
        gql(DefineBimClass),
        {
            awaitRefetchQueries: true,
            // Document, not the query name: this screen replaces the list rather
            // than sitting beside it, so a name had no active query to match.
            refetchQueries: [
                { query: gql(ListBimClasses) },
            ],
        },
    );

    async function onCreate(definition) {
        const result = await defineBimClass({ variables: { definition } });
        const payload = result.data?.defineBimClass;
        const bimClass = payload?.bimClass;
        return {
            errors: payload?.errors,
            redirectTo: bimClass?.id && `../${ bimClass.id }/revisions/${ bimClass.revisionID }`,
        };
    }

    return (
        <div className="section-card">
            <EntityCreateForm
                createLabel="Create BimClass"
                defaultValues={ DEFAULT_VALUES }
                jsonDescription="Raw BIM Class JSON definition (name, materialName, bimClassID, type, deprecated, previews, requirementGroups, requirements, properties)"
                onCreate={ onCreate }
                renderFields={ renderFields }
                title="New BimClass"
                toDefinition={ toDefinition }
            />
        </div>
    );
}
